/**
 * VerifyBulletinPage — Page publique de vérification d'un bulletin de paie
 * accessible via le QR code imprimé sur le PDF.
 * URL : /verify-bulletin/:bulletin_no
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyBulletinPage() {
  const { bulletin_no } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/verify-bulletin/${encodeURIComponent(bulletin_no)}`);
        setState({ loading: false, data: r.data, error: null });
      } catch (e) {
        setState({ loading: false, data: null, error: e?.response?.data?.detail || "Bulletin non reconnu" });
      }
    })();
  }, [bulletin_no]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #1F3661 0%, #0f172a 100%)" }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header brand */}
        <div className="flex items-center gap-3 px-5 py-4" style={{ background: "#1F3661" }}>
          <img src="/logo_espace_maxo.png" alt="Espace Maxo" className="h-11 w-auto" />
          <div>
            <p className="text-white font-black text-[15px] tracking-tight leading-tight">Vérification bulletin</p>
            <p className="text-white/70 text-[11px]">Espace Maxo SARL</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {state.loading ? (
            <div className="flex flex-col items-center py-8 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin mb-3" />
              <p className="text-sm">Vérification en cours…</p>
            </div>
          ) : state.error ? (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "#fef2f2", border: "3px solid #dc2626" }}>
                <XCircle className="w-10 h-10 text-red-600" strokeWidth={2.2} />
              </div>
              <p className="text-red-700 font-black text-[18px] mb-1">Bulletin non reconnu</p>
              <p className="text-slate-500 text-sm text-center">{state.error}</p>
              <p className="text-slate-400 text-[11px] font-mono mt-4">{bulletin_no}</p>
            </div>
          ) : (
            <div>
              <div className="flex flex-col items-center py-4 mb-4">
                {state.data.tampered ? (
                  <>
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ background: "#fef2f2", border: "3px solid #dc2626" }}>
                      <XCircle className="w-10 h-10 text-red-600" strokeWidth={2.2} />
                    </div>
                    <p className="text-red-700 font-black text-[18px]">Signature invalide</p>
                    <p className="text-slate-500 text-xs text-center mt-1">Ce bulletin a été modifié après verrouillage</p>
                  </>
                ) : state.data.signed ? (
                  <>
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ background: "#ecfdf5", border: "3px solid #10b981" }}>
                      <ShieldCheck className="w-10 h-10 text-emerald-600" strokeWidth={2.2} />
                    </div>
                    <p className="text-emerald-700 font-black text-[18px]">Bulletin authentique</p>
                    <p className="text-emerald-600 text-xs text-center mt-1">Verrouillé & signé (SHA-256)</p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-3" style={{ background: "#fefce8", border: "3px solid #eab308" }}>
                      <ShieldCheck className="w-10 h-10 text-amber-600" strokeWidth={2.2} />
                    </div>
                    <p className="text-amber-700 font-black text-[18px]">Bulletin reconnu</p>
                    <p className="text-amber-600 text-xs text-center mt-1">Non verrouillé — modifications possibles</p>
                  </>
                )}
              </div>
              <div className="space-y-2 text-[13px]">
                {[
                  ["N° bulletin", state.data.bulletin_number, "font-mono"],
                  ["Période", state.data.period],
                  ["Employé", state.data.employee_name, "font-bold"],
                  ["Matricule", state.data.matricule || "—", "font-mono"],
                  ["Poste", state.data.role_title || "—"],
                  ["Employeur", state.data.company, "font-bold"],
                  ["Fiche éditée", state.data.issued ? "Oui — enregistrée" : "En cours"],
                  ...(state.data.net_to_pay ? [["Net à payer", `${Math.round(state.data.net_to_pay).toLocaleString('fr-FR')} F`, "font-mono font-bold text-emerald-700"]] : []),
                ].map(([k, v, extra]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 pb-1.5">
                    <span className="text-slate-500">{k}</span>
                    <span className={`text-slate-800 ${extra || ""}`}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 text-center text-[10.5px] text-slate-400 border-t border-slate-100">
          Vérification en ligne · Espace Maxo Restaurant & Jeux VR · Cotonou, Bénin
        </div>
      </div>
    </div>
  );
}
